import { createFileRoute } from "@tanstack/react-router";
import { TableLoaderRow } from "@/components/ui/loader";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useFar } from "@/features/finance/api";
import { currentFy, fyOptions } from "@/features/finance/fy";
import { inr } from "@/features/finance/format";
import { Printer } from "lucide-react";
import { ReportExportButton } from "@/features/reports/export/ReportExportButton";
import { useBranch } from "@/context/BranchContext";

export const Route = createFileRoute("/_authenticated/finance/fixed-asset-register")({
  component: FarPage,
});

function FarPage() {
  const [fy, setFy] = useState(currentFy());
  const { currentBranch, currentBranchId } = useBranch();
  const { data, isLoading } = useFar(fy, currentBranchId);
  const rows = data ?? [];

  const totals = rows.reduce(
    (a, r) => ({
      gross: a.gross + Number(r.gross_block ?? 0),
      add: a.add + Number(r.additions ?? 0),
      del: a.del + Number(r.deletions ?? 0),
      depOpen: a.depOpen + Number(r.accum_dep_opening ?? 0),
      dep: a.dep + Number(r.dep_this_fy ?? 0),
      depClose: a.depClose + Number(r.accum_dep_closing ?? 0),
      nbv: a.nbv + Number(r.nbv_closing ?? 0),
    }),
    { gross: 0, add: 0, del: 0, depOpen: 0, dep: 0, depClose: 0, nbv: 0 },
  );

  return (
    <div className="space-y-4 print:space-y-2">
      <style>{`
        @media print {
          @page {
            size: A4 landscape;
            margin: 8mm 10mm;
          }
          body {
            background: white !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .no-print {
            display: none !important;
          }
          .far-print-container {
            max-height: none !important;
            overflow: visible !important;
          }
          .far-print-table {
            width: 100% !important;
            border-collapse: collapse !important;
            font-size: 10px !important;
          }
          .far-print-table th, .far-print-table td {
            padding: 5px 6px !important;
            border: 1px solid #cbd5e1 !important;
          }
          .far-print-table th {
            background-color: #f1f5f9 !important;
            color: #0f172a !important;
            font-weight: 700 !important;
          }
          .far-print-table tr {
            page-break-inside: avoid !important;
          }
        }
      `}</style>

      {/* Screen Header */}
      <div className="flex items-center justify-between print:hidden">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Fixed Asset Register</h1>
          <p className="text-sm text-muted-foreground">FY {fy} · {rows.length} assets</p>
        </div>
        <div className="flex items-center gap-2 no-print">
          <Select value={fy} onValueChange={setFy}>
            <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
            <SelectContent>{fyOptions().map((y) => <SelectItem key={y} value={y}>FY {y}</SelectItem>)}</SelectContent>
          </Select>
          <ReportExportButton
            reportKey="R-11"
            title={`FAR ${fy}`}
            filename={`fixed-asset-register-${fy}`}
            rows={() => rows.map((r) => ({
              "Asset Code": r.asset_code,
              "Asset Name": r.asset_name,
              "Category": r.category_name ?? "",
              "Capitalisation Date": r.capitalisation_date ?? "",
              "Gross Block": Number(r.gross_block ?? 0),
              "Additions": Number(r.additions ?? 0),
              "Deletions": Number(r.deletions ?? 0),
              "Accum. Dep Opening": Number(r.accum_dep_opening ?? 0),
              "Dep This FY": Number(r.dep_this_fy ?? 0),
              "Accum. Dep Closing": Number(r.accum_dep_closing ?? 0),
              "NBV Closing": Number(r.nbv_closing ?? 0),
            }))}
            requiredColumns={["Asset Code"]}
            variant="outline"
            size="default"
            disabled={!rows.length}
          />
          <Button variant="outline" onClick={() => window.print()}><Printer className="mr-1 h-4 w-4" />PDF</Button>
        </div>
      </div>

      {/* Print-Only Formal Document Header */}
      <div className="hidden print:block border-b-2 border-slate-800 pb-3 mb-2">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-lg font-bold text-slate-900 tracking-tight">HAMS — Hospital Asset Management System</div>
            <div className="text-sm font-semibold text-slate-700 mt-0.5">
              FIXED ASSET REGISTER (FAR){currentBranch ? ` — ${currentBranch.name}` : ""}
            </div>
          </div>
          <div className="text-right text-[11px] text-slate-600">
            <div>Financial Year: <span className="font-bold text-slate-900">FY {fy}</span></div>
            <div>Total Assets: <span className="font-bold text-slate-900">{rows.length}</span></div>
            <div>Printed: {new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</div>
          </div>
        </div>
      </div>

      <Card className="print:border-none print:shadow-none print:p-0">
        <CardHeader className="print:hidden"><CardTitle className="text-base">Register</CardTitle></CardHeader>
        <CardContent className="print:p-0">
          <div className="max-h-[70vh] overflow-auto far-print-container">
            <Table className="far-print-table">
              <TableHeader><TableRow className="bg-slate-50 print:bg-slate-100">
                <TableHead className="whitespace-nowrap font-semibold text-slate-900 print:text-[10px]">Code</TableHead>
                <TableHead className="whitespace-nowrap font-semibold text-slate-900 print:text-[10px]">Asset Name</TableHead>
                <TableHead className="whitespace-nowrap font-semibold text-slate-900 print:text-[10px]">Category</TableHead>
                <TableHead className="whitespace-nowrap font-semibold text-slate-900 print:text-[10px]">Cap. Date</TableHead>
                <TableHead className="text-right whitespace-nowrap font-semibold text-slate-900 print:text-[10px]">Gross Block</TableHead>
                <TableHead className="text-right whitespace-nowrap font-semibold text-slate-900 print:text-[10px]">Additions</TableHead>
                <TableHead className="text-right whitespace-nowrap font-semibold text-slate-900 print:text-[10px]">Deletions</TableHead>
                <TableHead className="text-right whitespace-nowrap font-semibold text-slate-900 print:text-[10px]">Acc. Dep Open</TableHead>
                <TableHead className="text-right whitespace-nowrap font-semibold text-slate-900 print:text-[10px]">Dep FY</TableHead>
                <TableHead className="text-right whitespace-nowrap font-semibold text-slate-900 print:text-[10px]">Acc. Dep Close</TableHead>
                <TableHead className="text-right whitespace-nowrap font-semibold text-slate-900 print:text-[10px]">NBV Close</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {isLoading && <TableLoaderRow colSpan={11} />}
                {rows.map((r) => (
                  <TableRow key={r.asset_id} className="print:border-b print:border-slate-300">
                    <TableCell className="font-mono text-xs whitespace-nowrap font-semibold text-slate-800 print:text-[10px]">{r.asset_code}</TableCell>
                    <TableCell className="text-xs whitespace-nowrap print:text-[10px]">{r.asset_name}</TableCell>
                    <TableCell className="text-xs whitespace-nowrap text-muted-foreground print:text-[10px]">{r.category_name ?? "—"}</TableCell>
                    <TableCell className="text-xs whitespace-nowrap print:text-[10px]">{r.capitalisation_date ?? "—"}</TableCell>
                    <TableCell className="text-right text-xs tabular-nums whitespace-nowrap print:text-[10px]">{inr(r.gross_block)}</TableCell>
                    <TableCell className="text-right text-xs tabular-nums whitespace-nowrap print:text-[10px]">{inr(r.additions)}</TableCell>
                    <TableCell className="text-right text-xs tabular-nums whitespace-nowrap print:text-[10px]">{inr(r.deletions)}</TableCell>
                    <TableCell className="text-right text-xs tabular-nums whitespace-nowrap print:text-[10px]">{inr(r.accum_dep_opening)}</TableCell>
                    <TableCell className="text-right text-xs tabular-nums whitespace-nowrap print:text-[10px]">{inr(r.dep_this_fy)}</TableCell>
                    <TableCell className="text-right text-xs tabular-nums whitespace-nowrap print:text-[10px]">{inr(r.accum_dep_closing)}</TableCell>
                    <TableCell className="text-right text-xs tabular-nums whitespace-nowrap font-semibold print:text-[10px]">{inr(r.nbv_closing)}</TableCell>
                  </TableRow>
                ))}
                {rows.length > 0 && (
                  <TableRow className="bg-slate-100 font-bold border-t-2 border-b-2 border-slate-700 print:bg-slate-100">
                    <TableCell colSpan={4} className="text-xs font-bold text-slate-900 print:text-[10px]">Totals ({rows.length} assets)</TableCell>
                    <TableCell className="text-right text-xs tabular-nums whitespace-nowrap font-bold print:text-[10px]">{inr(totals.gross)}</TableCell>
                    <TableCell className="text-right text-xs tabular-nums whitespace-nowrap font-bold print:text-[10px]">{inr(totals.add)}</TableCell>
                    <TableCell className="text-right text-xs tabular-nums whitespace-nowrap font-bold print:text-[10px]">{inr(totals.del)}</TableCell>
                    <TableCell className="text-right text-xs tabular-nums whitespace-nowrap font-bold print:text-[10px]">{inr(totals.depOpen)}</TableCell>
                    <TableCell className="text-right text-xs tabular-nums whitespace-nowrap font-bold print:text-[10px]">{inr(totals.dep)}</TableCell>
                    <TableCell className="text-right text-xs tabular-nums whitespace-nowrap font-bold print:text-[10px]">{inr(totals.depClose)}</TableCell>
                    <TableCell className="text-right text-xs tabular-nums whitespace-nowrap font-bold text-slate-900 print:text-[10px]">{inr(totals.nbv)}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

